// Import the functions you need from the SDKs you need
import { initializeApp } from "firebase/app";
import {
  initializeFirestore,
  connectFirestoreEmulator,
  persistentLocalCache,
  persistentMultipleTabManager
} from "firebase/firestore";
import { getAuth, connectAuthEmulator } from "firebase/auth";
import { getStorage, connectStorageEmulator } from "firebase/storage";
import { getAnalytics } from "firebase/analytics";
import isProductionHost from "../../helpers/isProductionHost";

// Local development must never touch production. USERS sits outside the
// constants.js toggle and Cloud Storage has no toggle at all, so completing a
// quiz locally used to overwrite a real user's certificate PDF.
//
// Layer 1 — the gate ALLOWLISTS production and defaults everything else to the
// emulator. Checking for "localhost" instead would fail *open*: Parcel binds
// every interface, so http://127.0.0.1:1234 and the LAN address serve the same
// dev server, and none of them is the string "localhost". An unrecognised host
// now gets the emulator and visibly fails to connect, which is the safe
// direction to be wrong in.
// The host list itself lives in helpers/isProductionHost.js, because the
// service-worker registration on the landing page needs the same answer and
// must not import the Firebase SDK to get it. One definition, so the two gates
// cannot drift apart. It covers deploy previews and branch URLs too.
const useEmulator = !isProductionHost();

// For Firebase JS SDK v7.20.0 and later, measurementId is optional.
// These keys are public by design; security rules are the real protection.
const productionConfig = {
  apiKey: "AIzaSyD-UNzyMpSGjfx7K1JvZGQVS_IvJnKT3EI",
  // Deliberately our own hostname, not tgmq-2d23c.firebaseapp.com.
  //
  // The SDK loads the sign-in handler from authDomain and then reads the result
  // back through an iframe on it. Pointing at firebaseapp.com makes that a
  // third-party origin, and browsers partition third-party storage, so the
  // iframe reads an empty store and getRedirectResult resolves null — sign-in
  // fails silently. netlify.toml proxies /__/* here, so the handler is
  // same-origin and no partitioning applies.
  //
  // location.hostname rather than a literal, so deploy previews proxy through
  // their own hostname and stay testable. Every host that reaches this config
  // is a Netlify host covered by that proxy, because isProductionHost() is what
  // selects it. Each such host needs to be an authorized domain in Firebase Auth
  // AND an authorized redirect URI (https://<host>/__/auth/handler) on the
  // Google OAuth client.
  authDomain: location.hostname,
  projectId: "tgmq-2d23c",
  storageBucket: "tgmq-2d23c.appspot.com",
  messagingSenderId: "363105704471",
  appId: "1:363105704471:web:4890b6f25751d7b068da52",
  measurementId: "G-WBTTJHC6PL"
};

// Layer 2 — a weak backstop, described precisely, because two earlier versions
// of this comment overstated it.
//
// What is NOT true: the SDK has no special handling for a "demo-" project id
// (that is a CLI convention), the api key does not protect anything here
// (@firebase/storage never transmits one), and requests do not "go nowhere" —
// they reach googleapis.com and are answered.
//
// What IS true: the GCP project demo-tgmq does not exist, so requests carrying
// it are rejected at the project level (PERMISSION_DENIED / 404) instead of
// touching live data.
//
// This is thin. THE REAL PROTECTION IS THE if (useEmulator) BLOCK BELOW.
// Keep the three connect*Emulator calls inside it. Do not treat this config as
// a standalone safety net: demo-tgmq is an unclaimed, globally-unique project
// id that ships in the public bundle, so if the connects were ever made
// conditional, the fallback would be an id a stranger could register.
const emulatorConfig = {
  ...productionConfig,
  apiKey: "not-a-real-key-emulator-only",
  authDomain: "demo-tgmq.firebaseapp.com",
  projectId: "demo-tgmq",
  storageBucket: "demo-tgmq.appspot.com"
};

const app = initializeApp(useEmulator ? emulatorConfig : productionConfig);

// Every Firebase client the app uses is created here and nowhere else.
// A client created in another file would not be covered by the gate below,
// which is exactly how a partial wiring keeps talking to production while
// looking isolated. Import these; never call getAuth/getStorage elsewhere.
// persistentLocalCache keeps documents in IndexedDB, so every getDoc in the app
// falls back to cache when offline, and the unawaited writes in
// firebaseSetDocWrapper queue durably and flush on reconnect instead of being
// lost. Unlike the deprecated enableIndexedDbPersistence it degrades to an
// in-memory cache on its own when IndexedDB is unavailable (private browsing),
// so there is nothing to catch here.
const db = initializeFirestore(app, {
  localCache: persistentLocalCache({ tabManager: persistentMultipleTabManager() })
});
const auth = getAuth(app);
const storage = getStorage(app);

if (useEmulator) {
  connectAuthEmulator(auth, "http://127.0.0.1:9099", { disableWarnings: true });
  connectFirestoreEmulator(db, "127.0.0.1", 8080);
  connectStorageEmulator(storage, "127.0.0.1", 9199);
}

// Analytics reports to the real property whatever the project id says, so it is
// production-only. Automatic collection starts here; logAnalyticsEvent.js reads
// this export to send the custom sign-in and round events, and relies on it
// being null under the emulator so local play sends nothing.
const analytics = useEmulator ? null : getAnalytics(app);

export { app, db, auth, storage, analytics };
